/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import { useIntl } from 'react-intl'

import { Button } from '../../../components/Button/Button'
import { GrainCard } from '../../../components/GrainCard'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { SMALL_BREAKPOINT } from '../../../utils/breakpoints'
import approveChangesIllustration from './assets/approve-changes.svg'
import regularMaintenanceIllustration from './assets/regular-maintenance.svg'
import reviewDozensIllustration from './assets/review-dozens.svg'

const TILE_PADDING_Y = 48
const TILE_PADDING_X = 48
const TILE_PADDING_MOBILE = 32

const gridCss = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    grid-template-columns: 1fr;
    & h3 {
      font-size: 28px;
    }
  }
`

const tileCss = css`
  display: flex;
  flex-direction: column;
  text-align: start;
  padding: ${TILE_PADDING_Y}px ${TILE_PADDING_X}px 0;

  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    padding: ${TILE_PADDING_MOBILE}px ${TILE_PADDING_MOBILE}px 0;
  }
`

const tileIllustrationCss = css`
  display: block;
  width: calc(100% + ${TILE_PADDING_X * 2}px);
  max-width: none;
  height: auto;
  margin: auto -${TILE_PADDING_X}px 0;
  padding-top: 32px;
  box-sizing: content-box;

  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    width: calc(100% + ${TILE_PADDING_MOBILE * 2}px);
    margin-left: -${TILE_PADDING_MOBILE}px;
    margin-right: -${TILE_PADDING_MOBILE}px;
  }
`

const tileTitleCss = { fontSize: 28, fontWeight: 500 } as const

export function SliteAgentBento({ hideCta }: { hideCta?: boolean } = {}) {
  const intl = useIntl()

  const tiles = [
    {
      title: intl.formatMessage({
        id: 'home.sliteAgent.bento.selfMaintain',
        defaultMessage: 'Review dozens of documents in minutes',
      }),
      description: intl.formatMessage({
        id: 'home.sliteAgent.bento.selfMaintain.sub',
        defaultMessage: 'Scheduled reviews. The dedicated owner signs off.',
      }),
      illustration: reviewDozensIllustration,
      illustrationAspectRatio: '590 / 466',
    },
    {
      title: intl.formatMessage({
        id: 'home.sliteAgent.bento.routines',
        defaultMessage: 'Run regular maintenance on your knowledge base',
      }),
      description: intl.formatMessage({
        id: 'home.sliteAgent.bento.routines.sub',
        defaultMessage: "Routine checks, flagged for your team's review.",
      }),
      illustration: regularMaintenanceIllustration,
      illustrationAspectRatio: '590 / 458',
    },
  ]

  return (
    <div css={gridCss}>
      {tiles.map((tile) => (
        <GrainCard key={tile.title} css={tileCss}>
          <WebsiteText
            variant="smallTitle"
            htmlTag="h3"
            className="font-family-title shade-00"
            style={{ ...tileTitleCss, maxWidth: 400 }}
          >
            {tile.title}
          </WebsiteText>
          <WebsiteText
            variant="body"
            className="shade-10 margin-top-8"
            style={{ maxWidth: 400 }}
          >
            {tile.description}
          </WebsiteText>
          <img
            src={tile.illustration}
            alt=""
            aria-hidden
            draggable={false}
            css={tileIllustrationCss}
            style={{ aspectRatio: tile.illustrationAspectRatio }}
          />
        </GrainCard>
      ))}

      <HumanLoopCard hideCta={hideCta} />
    </div>
  )
}

const humanLoopCardCss = css`
  grid-column: 1 / -1;
  display: flex;
  text-align: start;

  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    flex-direction: column;
  }
`

const humanLoopTextCss = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 80px 32px 80px 80px;

  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    padding: ${TILE_PADDING_MOBILE}px ${TILE_PADDING_MOBILE}px 0;
  }
`

// The Figma frame gives the screenshot 650 of the card's 1200px, bleeding off
// the right and bottom edges.
const humanLoopIllustrationCss = css`
  display: block;
  width: ${(650 / 1200) * 100}%;
  max-width: none;
  height: auto;
  aspect-ratio: 650 / 600;
  align-self: flex-end;

  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    width: 100%;
    margin-top: 32px;
  }
`

export function HumanLoopCard({
  hideCta,
  title,
}: {
  hideCta?: boolean
  title?: string
}) {
  const intl = useIntl()
  const routes = useRoutes()

  return (
    <GrainCard css={humanLoopCardCss}>
      <div css={humanLoopTextCss}>
        <WebsiteText
          variant="smallTitle"
          htmlTag="h3"
          className="font-family-title shade-00"
          style={{ ...tileTitleCss, maxWidth: 300 }}
        >
          {title ??
            intl.formatMessage({
              id: 'home.sliteAgent.bento.humanLoop.title',
              defaultMessage: 'Approve changes in one click',
            })}
        </WebsiteText>
        <WebsiteText
          variant="body"
          className="shade-10"
          style={{ maxWidth: 400 }}
        >
          {intl.formatMessage({
            id: 'home.sliteAgent.bento.humanLoop.desc',
            defaultMessage:
              'Slite Agent drafts the change and sends it to the document owner. Nothing goes out unchecked.',
          })}
        </WebsiteText>
        {!hideCta && (
          <Button
            href={routes.blog.agentAnnouncement()}
            variant="secondary"
            withChevron
            style={{ alignSelf: 'flex-start', marginTop: 16 }}
          >
            {intl.formatMessage({
              id: 'home.sliteAgent.bento.humanLoop.cta',
              defaultMessage: 'See how it works',
            })}
          </Button>
        )}
      </div>
      <img
        src={approveChangesIllustration}
        alt=""
        aria-hidden
        draggable={false}
        css={humanLoopIllustrationCss}
      />
    </GrainCard>
  )
}
